import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Dialog} from '../../../../../../../../../src/components/common/dialogs/dialog';
import {DialogContent} from '../../../../../../../../../src/components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../../../../../../../src/components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../../../../../../../src/components/common/dialogs/dialogHeader';
import {LinkTrap} from '../../../../../../../../../src/components/common/linkTrap/linkTrap';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../../../src/services/tracking';
import {ContactsManagerContactsCsvFileState} from '../../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileState';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {Handler, HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../../shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../../../shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../../shared/ui/src/theme/userThemeProvider';
import {DragAndDrop} from '../../../common/dragAndDrop/dragAndDrop';
import {DragAndDropAttachment} from '../../../common/dragAndDrop/dragAndDropAttachment';
import {ImportCsvHelpBannerTypesEnum} from '../importCSVHelpBanner';
import {ImportCSVHelpBannerContainer} from '../importCSVHelpBannerContainer';
import {
  contactsCSVTemplateUrl,
  contactsManagerImportDialogHeight,
  renderContactsCSVError,
} from '../importDialogHelpers';
import {ImportContactsNamespaceDropdown} from './fields/importContactsNamespaceDropdown';

/*
 * Props.
 */

interface ImportContactsCsvDialogProps {
  isInfoBoxExpanded: boolean;
  fileState: ContactsManagerContactsCsvFileState | undefined;
  userNamespace: string;
  initialNamespace: string;
  parameterType: ContactsManagerParametersTypesEnum;

  onRequestClose: Handler;
  onToggleInfoBanner: Handler;
  uploadContactsCsvAsync: (file: File, namespace: string, dryRun?: boolean) => Promise<void>;
  clearFile: Handler;
  clearJobs: Handler;
}

/*
 * Style.
 */

const StyledAttachmentsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledDialogContent = styled(DialogContent)`
  flex: 1;
  overflow: auto;
  margin: 0 20px;
  padding: 20px 0;
`;

const StyledCancelButton = styled(Button)`
  margin-left: auto;
`;

const StyledImportButton = styled(Button)`
  margin-left: 10px;
`;

const StyledErrorsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  margin-top: 8px;
`;

const StyledErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};

  a {
    color: ${(p) => p.theme.palette.red.shade40};
    font-weight: ${fontWeights.semibold};
    text-decoration: underline;
  }
  &:not(:first-child) {
    margin-top: 8px;
  }
`;

/*
 * Component.
 */

export const ImportContactsCSVDialog: FC<ImportContactsCsvDialogProps> = (props) => {
  const theme = useUserTheme();
  const {
    fileState,
    isInfoBoxExpanded,
    userNamespace,
    initialNamespace,
    parameterType,
    onRequestClose,
    clearFile,
    onToggleInfoBanner,
    uploadContactsCsvAsync,
    clearJobs,
  } = props;
  const [namespace, setNamespace] = useState(initialNamespace);
  const iconColor = isInfoBoxExpanded ? theme.palette.blue.shade40 : undefined;
  const isImporting = fileState?.attachment.uploadStatus === AsyncStatusesEnum.LOADING;

  const onClose = () => {
    onRequestClose();
    clearFile();
  };

  const onImport = async () => {
    if (fileState?.file) {
      try {
        await uploadContactsCsvAsync(fileState.file, namespace, false);
        onRequestClose();
        clearJobs();
        trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_IMPORT, {
          section_type: parameterType,
          namespace,
        });
      } catch {
        // Do not close when we error.
      }
    }
  };

  return (
    <Dialog onRequestClose={onClose} height={contactsManagerImportDialogHeight}>
      <DialogHeader
        title={<Trans id="contacts-manager-import-contacts-dialog-header">Import contacts</Trans>}
        customButtons={
          <IconButton
            iconName={isInfoBoxExpanded ? 'infoFilled' : 'info'}
            label={
              <Trans id="contacts-manager-import-contacts-dialog-header-more-info-button">
                More information
              </Trans>
            }
            onClick={onToggleInfoBanner}
            color={iconColor}
            hoverColor={iconColor}
          />
        }
      />
      <StyledDialogContent>
        {isInfoBoxExpanded && (
          <ImportCSVHelpBannerContainer
            type={ImportCsvHelpBannerTypesEnum.CONTACTS}
            learnMoreUrl="https://help.front.com/t/3624vh/import-or-upload-contacts-in-front"
            CSVDownloadUrl={contactsCSVTemplateUrl}
            onRequestClose={onToggleInfoBanner}
          />
        )}
        {maybeRenderDragAndDrop((file) => uploadContactsCsvAsync(file, namespace), fileState)}
        {fileState && (
          <>
            <StyledAttachmentsContainerDiv>
              <DragAndDropAttachment
                attachment={fileState.attachment}
                onRemoveClick={() => clearFile()}
                canBeRemoved
                hasError={fileState.attachment.uploadStatus === AsyncStatusesEnum.FAILED}
              />
            </StyledAttachmentsContainerDiv>
            {maybeRenderErrors(fileState)}
          </>
        )}
        <ImportContactsNamespaceDropdown
          namespace={namespace}
          userNamespace={userNamespace}
          onNamespaceSelect={setNamespace}
        />
      </StyledDialogContent>
      <DialogFooter>
        <StyledCancelButton onClick={onClose}>
          <Trans id="contacts-manager-import-contacts-dialog-cancel-button">Cancel</Trans>
        </StyledCancelButton>
        <StyledImportButton
          onClick={onImport}
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={
            !fileState || fileState.attachment.uploadStatus !== AsyncStatusesEnum.LOADED || isImporting
          }
        >
          <Trans id="contacts-manager-import-contacts-dialog-import-csv-button">Import CSV</Trans>
        </StyledImportButton>
      </DialogFooter>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function maybeRenderDragAndDrop(
  uploadContactsCSVAsync: HandlerOf<File>,
  fileState: ContactsManagerContactsCsvFileState | undefined,
) {
  if (
    fileState &&
    (fileState.attachment.uploadStatus === AsyncStatusesEnum.LOADED ||
      fileState.attachment.uploadStatus === AsyncStatusesEnum.LOADING)
  ) {
    return null;
  }
  return (
    <DragAndDrop
      onDropFile={(file) => uploadContactsCSVAsync(file)}
      isUploading={Boolean(fileState && fileState.attachment.uploadStatus === AsyncStatusesEnum.LOADING)}
      hasError={Boolean(fileState && fileState.attachment.uploadStatus === AsyncStatusesEnum.FAILED)}
    />
  );
}

function maybeRenderErrors(fileState: ContactsManagerContactsCsvFileState) {
  if (!fileState.errors || fileState.errors.length === 0) {
    return null;
  }
  return (
    <StyledErrorsContainerDiv>
      <LinkTrap>
        {fileState.errors.map((error) => (
          <StyledErrorDiv key={error.error}>
            {renderContactsCSVError(error, fileState.file, contactsCSVTemplateUrl)}
          </StyledErrorDiv>
        ))}
      </LinkTrap>
    </StyledErrorsContainerDiv>
  );
}
